import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { CampaignInfoStore } from '../../../../../../stores/campaign-info.store';
import { ChoiceListElementStore } from '../../../../../../stores/choice-list-element.store';
import { InfoChoiceDataStore } from '../../../../../../stores/info-choice-data.store';
import { InfoChoiceStore } from '../../../../../../stores/info-choice.store';
import { InfoFileDataStore } from '../../../../../../stores/info-file-data.store';
import { InfoLocationStore } from '../../../../../../stores/info-location.store';
import { InfoTextDataStore } from '../../../../../../stores/info-text-data.store';
import { InfoStore } from '../../../../../../stores/info.store';
import { LocationStore } from '../../../../../../stores/location.store';
import type { AuscultationDataSchema } from '../../../../../../services/api/generated/model';
import { CampaignsService } from '../../../../../../services/api/generated/campaigns/campaigns.service';
import { CampaignInfo } from '../../../../../../types/campaign.interface';
import { ChoiceListElement } from '../../../../../../types/choice-list-element.interface';
import {
  Info,
  InfoChoice,
  InfoChoiceData,
  InfoFileData,
  InfoLocation,
  InfoTextData,
  InfoType,
} from '../../../../../../types/info.interface';
import { Location } from '../../../../../../types/location.interface';

// Shape varies by Info.type: TEXT=string|null, CHOICE=number|null (choice_list_element.id),
// FILE/IMAGE=InfoFileData[]|null.
export type AuscultationFieldValue = string | number | InfoFileData[] | null;

/** Pending edit held in `globalPendingChanges`. Files use a separate pending-operations map. */
export type AuscultationPendingValue = string | number | null;

export interface AuscultationValue {
  campaignInfo: CampaignInfo;
  info: Info;
  value: AuscultationFieldValue;
  type: InfoType;
  locationsIds: number[];
  locationNames?: string;
}

/** Carries the file name and HTTP status of a failed upload so the page can tell the user why. */
export class FileUploadError extends Error {
  constructor(
    readonly fileName: string,
    readonly status: number,
  ) {
    super(`Upload of ${fileName} failed with status ${status}`);
  }
}

export interface PendingFileOperation {
  type: 'add' | 'remove';
  file?: File;
  fileDataId?: number;
  fileName?: string;
  fileType?: string;
  tempId?: string;
}

export interface DisplayedFile {
  id: string | number;
  name: string;
  path: string;
  mimeType: string;
  isPending: boolean;
  isExisting: boolean;
  campaignId?: number;
  infoId?: number;
  pendingRemoval?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignAuscultationService {
  readonly campaignInfoStore = inject(CampaignInfoStore);
  readonly infoStore = inject(InfoStore);
  readonly infoLocationStore = inject(InfoLocationStore);
  readonly infoChoiceDataStore = inject(InfoChoiceDataStore);
  readonly infoChoiceStore = inject(InfoChoiceStore);
  readonly infoTextDataStore = inject(InfoTextDataStore);
  readonly infoFileDataStore = inject(InfoFileDataStore);
  readonly locationStore = inject(LocationStore);
  readonly choiceListElementStore = inject(ChoiceListElementStore);
  private readonly campaigns = inject(CampaignsService);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  private readonly _currentCampaignId = signal<number | null>(null);
  private readonly _currentAssetId = signal<number | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _refreshTrigger = signal(0);

  private readonly _globalPendingChanges = signal<Map<number, AuscultationPendingValue>>(new Map());
  private readonly _validationErrors = signal<Map<number, string>>(new Map());
  private readonly _pendingFileOperations = signal<Map<number, PendingFileOperation[]>>(new Map());

  readonly globalPendingChanges = this._globalPendingChanges.asReadonly();
  readonly validationErrors = this._validationErrors.asReadonly();
  readonly pendingFileOperations = this._pendingFileOperations.asReadonly();

  readonly hasGlobalChanges = computed(() => {
    return this._globalPendingChanges().size > 0 || this._pendingFileOperations().size > 0;
  });

  readonly campaignInfos = computed<CampaignInfo[]>(() => {
    this._refreshTrigger();
    const campaignId = this._currentCampaignId();

    if (!campaignId) {
      return [];
    }

    const allEntities = this.campaignInfoStore.entities();
    const filtered = allEntities.filter((ci: CampaignInfo) => ci.campaignId === campaignId);

    return filtered;
  });

  readonly assetInfos = computed(() => {
    const assetId = this._currentAssetId();
    if (!assetId) return [];

    return this.infoStore.entities().filter((info: Info) => info.assetId === assetId);
  });

  readonly auscultationValues = computed(() => {
    this.translations();
    const campaignInfos = this.campaignInfos();
    if (!campaignInfos.length) return [];

    return campaignInfos.map(ci => this.buildAuscultationValue(ci)).filter((av): av is AuscultationValue => av !== null);
  });

  private buildAuscultationValue(campaignInfo: CampaignInfo): AuscultationValue | null {
    const info = this.infoStore.byId()(campaignInfo.infoId);
    if (!info) return null;

    const locationIds = this.infoLocationStore
      .entities()
      .filter((il: InfoLocation) => il.infoId === info.id)
      .map((il: InfoLocation) => il.locationId);

    const joinedLocationNames = this.locationStore
      .entities()
      .filter((loc: Location) => locationIds.includes(loc.id))
      .map((loc: Location) => loc.name)
      .filter((name: string | undefined) => name?.trim())
      .join(', ');
    const locationNames =
      joinedLocationNames.length > 0 ? joinedLocationNames : this.translate.translate('common.messages.noLocation');

    return {
      campaignInfo,
      info,
      value: this.getInfoValue(info, campaignInfo.campaignId),
      type: info.type,
      locationsIds: locationIds,
      locationNames,
    };
  }

  private getInfoValue(info: Info, campaignId: number): AuscultationFieldValue {
    switch (info.type) {
      case InfoType.TEXT:
        return (
          this.infoTextDataStore.entities().find((td: InfoTextData) => td.infoId === info.id && td.campaignId === campaignId)?.value ??
          null
        );
      case InfoType.CHOICE:
        return (
          this.infoChoiceDataStore.entities().find((cd: InfoChoiceData) => cd.infoId === info.id && cd.campaignId === campaignId)
            ?.choiceListElementId ?? null
        );
      case InfoType.FILE:
      case InfoType.IMAGE: {
        const files = this.infoFileDataStore
          .entities()
          .filter((fd: InfoFileData) => fd.infoId === info.id && fd.campaignId === campaignId);
        return files.length ? files : null;
      }
      default:
        return null;
    }
  }

  setCampaignId(campaignId: number): void {
    this._currentCampaignId.set(campaignId);
  }

  setAssetId(assetId: number): void {
    this._currentAssetId.set(assetId);
  }

  async loadAuscultationData(campaignId: number, assetId: number): Promise<void> {
    this._isLoading.set(true);
    this.setCampaignId(campaignId);
    this.setAssetId(assetId);

    try {
      const [, assetInfos] = await Promise.all([
        this.campaignInfoStore.getByCampaignId(campaignId),
        this.infoStore.getByAssetId(assetId),
        this.infoChoiceDataStore.getByCampaignId(campaignId),
        this.infoTextDataStore.getByCampaignId(campaignId),
        this.infoFileDataStore.getByCampaignId(campaignId),
        this.locationStore.getByAssetId(assetId),
        this.choiceListElementStore.getByAssetId(assetId),
      ]);
      const infoIds = assetInfos.map((info: Info) => info.id);

      await Promise.all([this.loadInfoChoicesForInfos(infoIds), this.loadInfoLocationsForInfos(infoIds)]);

      this.refreshCampaignInfosWithDetails();
      this._globalPendingChanges.set(new Map());
      this._validationErrors.set(new Map());
      this._pendingFileOperations.set(new Map());
    } finally {
      this._isLoading.set(false);
    }
  }

  private async loadInfoChoicesForInfos(infoIds: number[]): Promise<void> {
    if (!infoIds.length) return;
    await this.infoChoiceStore.getByInfoIds(infoIds);
  }

  private async loadInfoLocationsForInfos(infoIds: number[]): Promise<void> {
    if (!infoIds.length) return;
    await this.infoLocationStore.getByInfoIds(infoIds);
  }

  readonly isLoading = this._isLoading.asReadonly();

  getCurrentValue(campaignInfoId: number): AuscultationFieldValue {
    const pendingValue = this._globalPendingChanges().get(campaignInfoId);
    if (pendingValue !== undefined) {
      return pendingValue;
    }

    const auscultationValue = this.auscultationValues().find((av: AuscultationValue) => av.campaignInfo.id === campaignInfoId);
    return auscultationValue?.value ?? null;
  }

  updatePendingValue(campaignInfoId: number, value: AuscultationPendingValue): void {
    const changes = this._globalPendingChanges();
    const newChanges = new Map(changes);

    const auscultationValue = this.auscultationValues().find((av: AuscultationValue) => av.campaignInfo.id === campaignInfoId);
    const originalValue = auscultationValue?.value;

    if (value !== originalValue) {
      newChanges.set(campaignInfoId, value);
    } else {
      newChanges.delete(campaignInfoId);
    }

    this._globalPendingChanges.set(newChanges);

    const errors = this._validationErrors();
    if (errors.has(campaignInfoId)) {
      const newErrors = new Map(errors);
      newErrors.delete(campaignInfoId);
      this._validationErrors.set(newErrors);
    }
  }

  addPendingFile(campaignInfoId: number, file: File): void {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    this.updateFileOperations(campaignInfoId, ops => [
      ...ops,
      { type: 'add', file, fileName: file.name, fileType: file.type, tempId },
    ]);
  }

  removePendingFile(campaignInfoId: number, fileId: string | number, isPending: boolean): void {
    if (isPending) {
      this.updateFileOperations(campaignInfoId, ops => ops.filter(op => op.tempId !== fileId));
    } else {
      const existingFile = this.infoFileDataStore.byId()(fileId as number);
      const fileName = existingFile?.filename ?? existingFile?.path?.split('/').pop();
      this.updateFileOperations(campaignInfoId, ops => [
        ...ops,
        { type: 'remove', fileDataId: fileId as number, ...(fileName !== undefined && { fileName }) },
      ]);
    }
  }

  private updateFileOperations(campaignInfoId: number, updater: (ops: PendingFileOperation[]) => PendingFileOperation[]): void {
    const current = this._pendingFileOperations();
    const operations = current.get(campaignInfoId) ?? [];
    const updated = updater(operations);

    const newMap = new Map(current);
    if (updated.length === 0) {
      newMap.delete(campaignInfoId);
    } else {
      newMap.set(campaignInfoId, updated);
    }
    this._pendingFileOperations.set(newMap);
  }

  getDisplayedFiles(campaignInfoId: number): Signal<DisplayedFile[]> {
    return computed(() => {
      const campaignInfo = this.campaignInfos().find(ci => ci.id === campaignInfoId);
      if (!campaignInfo) return [];

      const existingFiles = this.infoFileDataStore
        .entities()
        .filter((file: InfoFileData) => file.infoId === campaignInfo.infoId && file.campaignId === campaignInfo.campaignId);

      const operations = this._pendingFileOperations().get(campaignInfoId) ?? [];

      const filesToRemove = operations.filter(op => op.type === 'remove').map(op => op.fileDataId);

      const visibleExisting: DisplayedFile[] = existingFiles.map((file: InfoFileData) => ({
        id: file.id,
        name: file.filename ?? file.path.split('/').pop() ?? 'Fichier sans nom',
        path: file.path,
        mimeType: file.mimeType,
        isPending: filesToRemove.includes(file.id),
        isExisting: true,
        campaignId: file.campaignId,
        infoId: file.infoId,
        pendingRemoval: filesToRemove.includes(file.id),
      }));

      const pendingNew: DisplayedFile[] = operations.flatMap<DisplayedFile>(op => {
        if (op.type !== 'add' || !op.tempId || !op.fileName) {
          return [];
        }
        return [
          {
            id: op.tempId,
            name: op.fileName,
            path: `temp/${op.fileName}`,
            mimeType: op.fileType ?? '',
            isPending: true,
            isExisting: false,
            pendingRemoval: false,
          },
        ];
      });

      return [...visibleExisting, ...pendingNew];
    });
  }

  async saveAllChanges(): Promise<void> {
    const textChoiceChanges = this._globalPendingChanges();
    const fileOperations = this._pendingFileOperations();

    if (textChoiceChanges.size === 0 && fileOperations.size === 0) {
      return;
    }

    this._isLoading.set(true);
    this._validationErrors.set(new Map());

    try {
      const campaignId = this._currentCampaignId();

      if (textChoiceChanges.size > 0 && campaignId) {
        const textChanges: Array<{ infoId: number; value: string | null }> = [];
        const choiceChanges: Array<{ infoId: number; choiceListElementId: number | null }> = [];

        for (const [campaignInfoId, value] of textChoiceChanges.entries()) {
          const campaignInfo = this.campaignInfos().find(ci => ci.id === campaignInfoId);
          if (!campaignInfo) continue;
          const info = this.infoStore.byId()(campaignInfo.infoId);
          if (!info) continue;

          if (info.type === InfoType.TEXT) {
            const trimmed = typeof value === 'string' ? value.trim() : '';
            textChanges.push({ infoId: info.id, value: trimmed.length > 0 ? trimmed : null });
          } else if (info.type === InfoType.CHOICE) {
            choiceChanges.push({ infoId: info.id, choiceListElementId: typeof value === 'number' ? value : null });
          }
        }

        // Backend treats null as "clear"; the schema declares value/choiceListElementId optional only.
        const payload = { textChanges, choiceChanges } as unknown as AuscultationDataSchema;
        await firstValueFrom(this.campaigns.putCampaignAuscultationDataResource({ id: campaignId }, payload));
        this._globalPendingChanges.set(new Map());
        await Promise.all([
          this.infoTextDataStore.getByCampaignId(campaignId, { forceRefresh: true }),
          this.infoChoiceDataStore.getByCampaignId(campaignId),
        ]);
      }

      for (const [campaignInfoId, operations] of fileOperations.entries()) {
        await this.saveFileOperations(campaignInfoId, operations);
      }

      this._globalPendingChanges.set(new Map());
      this._pendingFileOperations.set(new Map());
      this.refreshCampaignInfosWithDetails();
    } finally {
      this._isLoading.set(false);
    }
  }

  cancelAllChanges(): void {
    this._globalPendingChanges.set(new Map());
    this._validationErrors.set(new Map());
    this._pendingFileOperations.set(new Map());
  }

  private async saveFileOperations(campaignInfoId: number, operations: PendingFileOperation[]): Promise<void> {
    const campaignInfo = this.campaignInfos().find(ci => ci.id === campaignInfoId);
    if (!campaignInfo) {
      throw new Error(this.translate.translate('common.errors.campaignInfoNotFound'));
    }

    for (const operation of operations.filter(op => op.type === 'remove')) {
      if (operation.fileDataId) {
        await this.infoFileDataStore.delete(operation.fileDataId);
        this.dropFileOperation(campaignInfoId, operation);
      }
    }

    const primaryAssetInfo = this.assetInfos()[0];
    for (const operation of operations.filter(op => op.type === 'add')) {
      if (operation.file && operation.fileName && primaryAssetInfo) {
        try {
          const fileData = await this.infoFileDataStore.saveFile(
            operation.file,
            primaryAssetInfo.assetId,
            campaignInfo.campaignId,
            campaignInfo.infoId,
          );
          await this.infoFileDataStore.create({
            name: '',
            filename: operation.fileName,
            infoId: campaignInfo.infoId,
            campaignId: campaignInfo.campaignId,
            path: fileData.path,
            mimeType: operation.fileType ?? '',
          });
        } catch (error) {
          throw error instanceof HttpErrorResponse ? new FileUploadError(operation.fileName, error.status) : error;
        }
        this.dropFileOperation(campaignInfoId, operation);
      }
    }
  }

  // A saved operation leaves the queue at once: retrying after a failure must not
  // resend files already stored, which the API would refuse as duplicates.
  private dropFileOperation(campaignInfoId: number, operation: PendingFileOperation): void {
    this.updateFileOperations(campaignInfoId, ops => ops.filter(op => op !== operation));
  }

  getEditableChoiceOptions(info: Info): Signal<ChoiceListElement[]> {
    return computed(() => {
      const infoChoice = this.infoChoiceStore.entities().find((ic: InfoChoice) => ic.infoId === info.id);
      if (!infoChoice) return [];

      return this.choiceListElementStore
        .entities()
        .filter((el: ChoiceListElement) => el.choiceListId === infoChoice.choiceListId)
        .sort((a: ChoiceListElement, b: ChoiceListElement) => {
          if (a.sortNr !== null && b.sortNr !== null) return a.sortNr - b.sortNr;
          if (a.sortNr !== null) return -1;
          if (b.sortNr !== null) return 1;
          return a.name.localeCompare(b.name);
        });
    });
  }

  private refreshCampaignInfosWithDetails(): void {
    this._refreshTrigger.update(value => value + 1);
  }

  cancelPendingFileRemoval(campaignInfoId: number, fileId: string | number): void {
    this.updateFileOperations(campaignInfoId, ops => ops.filter(op => !(op.type === 'remove' && op.fileDataId === fileId)));
  }

  getChoiceLabelById(choiceId: number | string | null): string | null {
    if (choiceId === null || choiceId === '') return null;
    const id = typeof choiceId === 'string' ? parseInt(choiceId, 10) : choiceId;
    if (isNaN(id)) return null;
    return this.choiceListElementStore.byId()(id)?.name ?? null;
  }
}
